import { apiRequest, hasSession } from "./api";

/**
 * Product analytics (user stories, "Analytics and Operational Events"). Events are queued and
 * posted in small batches so a click never waits on the network; the API keeps only the property
 * keys declared for each event, so nothing personal can leak even by mistake here.
 */

export type AnalyticsEventName =
  | "trip_reserve_clicked" | "reservation_modal_opened" | "reservation_request_failed"
  | "apartado_offer_viewed" | "apartado_offer_selected" | "planning_route_selected" | "wallet_goal_saved"
  | "apartado_transfer_started" | "apartado_transfer_failed"
  | "reservation_history_viewed" | "reservation_history_searched" | "reservation_history_request_opened" | "apartado_started_from_history"
  | "apartado_goal_change_started" | "apartado_fund_disposal_started"
  | "personalized_trip_card_clicked" | "personalized_trip_step_completed"
  | "saved_trips_viewed";

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

interface QueuedEvent { name: AnalyticsEventName; properties: AnalyticsProperties; occurredAt: string }
export type AnalyticsSender = (batch: { sessionId: string; events: QueuedEvent[] }) => Promise<unknown>;

const FLUSH_DELAY_MS = 1_500;
const MAX_BATCH = 20;

function sessionIdentifier(): string {
  try {
    const existing = window.sessionStorage.getItem("av.analytics.session");
    if (existing) return existing;
    const created = crypto.randomUUID();
    window.sessionStorage.setItem("av.analytics.session", created);
    return created;
  } catch {
    return "no-storage";
  }
}

/** Builds a tracker around a sender; exported so tests can observe the batching without a network. */
export function createTracker(send: AnalyticsSender, options: { flushDelayMs?: number; maxBatch?: number; now?: () => Date } = {}) {
  const delay = options.flushDelayMs ?? FLUSH_DELAY_MS;
  const maxBatch = options.maxBatch ?? MAX_BATCH;
  const now = options.now ?? (() => new Date());
  let queue: QueuedEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let sessionId: string | null = null;

  const flush = async (): Promise<void> => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length) return;
    const events = queue.splice(0, maxBatch);
    try {
      await send({ sessionId: sessionId ?? (sessionId = sessionIdentifier()), events });
    } catch {
      // Analytics never surfaces errors to the customer; a lost batch is acceptable.
    }
    if (queue.length) void flush();
  };

  const track = (name: AnalyticsEventName, properties: AnalyticsProperties = {}): void => {
    const clean: AnalyticsProperties = {};
    for (const [key, value] of Object.entries(properties)) if (value !== undefined) clean[key] = value;
    queue.push({ name, properties: clean, occurredAt: now().toISOString() });
    if (queue.length >= maxBatch) { void flush(); return; }
    if (!timer) timer = setTimeout(() => { void flush(); }, delay);
  };

  return { track, flush, pending: () => queue.length, reset: () => { queue = []; if (timer) { clearTimeout(timer); timer = null; } } };
}

// Events are recorded per customer; a guest's clicks are dropped rather than sent to be refused.
const tracker = createTracker(async (batch) => {
  if (!hasSession()) return;
  await apiRequest("/api/v1/analytics/events", { method: "POST", body: JSON.stringify(batch), keepalive: true });
});

/** Records one product event. Safe to call anywhere; it never throws and never blocks. */
export const track = tracker.track;

// Whatever is still queued goes out when the tab is hidden or closed (keepalive lets it finish).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") void tracker.flush(); });
}
